import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMobile } from '../../hooks/useMobile';
import { StepPagination } from './StepPagination';
import { NotePickerMenu } from './NotePickerMenu';
import './StepGrid.css';
/**
 * Props for StepGrid component
 *
 * Requirements: 4.2, 9.1, 9.2, 9.3, 9.4, 9.5, 9.6, 10.6, 10.7
 */
export interface StepGridProps {
    /** Track index (0-based) */
    trackIndex: number;
    /** Grid state for this track (boolean array) */
    gridState: boolean[];
    /** Note state for this track (string | null array) */
    noteState: (string | null)[];
    /** Total number of steps in the composition */
    steps: number;
    /** Current playback step (0-based, -1 if not playing) */
    currentStep: number;
    /** Whether playback is currently active */
    isPlaying: boolean;
    /** Current page for pagination (0-based) */
    currentPage: number;
    /** Callback when cell is toggled */
    onCellToggle: (step: number) => void;
    /** Callback when note is changed */
    onNoteChange: (step: number, note: string) => void;
    /** Callback when page changes (for pagination) */
    onPageChange: (page: number) => void;
}
/**
 * Drag state for drag-to-paint functionality
 */
interface DragState {
    isDragging: boolean;
    dragStart: number | null;
    dragEnd: number | null;
    dragValue: boolean | null; // The value to paint (true or false)
}
/**
 * Note picker state
 */
interface NotePickerState {
    isOpen: boolean;
    step: number | null;
}
/**
 * Double-tap state for detecting double-tap gestures
 */
interface DoubleTapState {
    lastTapTime: number;
    lastTapStep: number | null;
}
/**
 * Get note color and hue based on pitch
 * Maps notes to a color spectrum with fine-grained hue variations
 */
function getNoteColorData(note: string | null): {
    color: string;
    hue: number;
} {
    if (!note)
        return { color: 'default', hue: 0 };
    // Extract note name and octave
    const match = note.match(/^([A-G]#?)(\d+)$/);
    if (!match)
        return { color: 'default', hue: 0 };
    const noteName = match[1];
    const octaveStr = match[2];
    if (!noteName || !octaveStr)
        return { color: 'default', hue: 0 };
    const octave = parseInt(octaveStr, 10);
    // Map note names to chromatic scale (0-11)
    const noteMap: Record<string, number> = {
        'C': 0, 'C#': 1, 'D': 2, 'D#': 3, 'E': 4, 'F': 5,
        'F#': 6, 'G': 7, 'G#': 8, 'A': 9, 'A#': 10, 'B': 11
    };
    const noteValue = noteMap[noteName] ?? 0;
    // Calculate MIDI note number (C4 = 60)
    const midiNote = (octave + 1) * 12 + noteValue;
    // Map MIDI note to hue with balanced variations
    const minMidi = 24; // C1
    // Calculate base hue from octave (major color shifts)
    const octaveRange = Math.floor((midiNote - minMidi) / 12);
    const baseHue = octaveRange * 45; // 45° per octave
    // Add variation within octave based on note position
    const noteWithinOctave = noteValue / 12;
    const hueVariation = noteWithinOctave * 30;
    const hue = Math.round(baseHue + hueVariation);
    // Determine color category for base styling
    let color = 'default';
    if (midiNote < 36)
        color = 'red';
    else if (midiNote < 48)
        color = 'orange';
    else if (midiNote < 60)
        color = 'yellow';
    else if (midiNote < 72)
        color = 'green';
    else if (midiNote < 84)
        color = 'cyan';
    else
        color = 'purple';
    return { color, hue };
}
/**
 * StepGrid Component
 *
 * Renders step grid for the selected track only in mobile layout.
 * Supports tap to toggle, drag-to-paint, and dynamically calculates how many
 * steps to show per page based on available vertical space.
 * Cell size is calculated dynamically to fill screen width with minimum 44x44px.
 *
 * The grid shows 8 columns and dynamically calculates rows (2-8) based on viewport height.
 * When total steps exceed what can fit on one page, displays StepPagination for navigation.
 * Auto-switches page when playback reaches a step on a different page.
 *
 * Requirements: 4.2, 9.1, 9.2, 9.3, 9.4, 9.5, 9.6, 10.6, 10.7
 */
export const StepGrid: React.FC<StepGridProps> = ({ trackIndex, gridState, noteState, steps, currentStep, isPlaying, currentPage, onCellToggle, onNoteChange, onPageChange, }) => {
    const {} = useMobile();
    const gridRef = useRef<HTMLDivElement>(null);
    const [dragState, setDragState] = useState<DragState>({
        isDragging: false,
        dragStart: null,
        dragEnd: null,
        dragValue: null,
    });
    const [notePickerState, setNotePickerState] = useState<NotePickerState>({
        isOpen: false,
        step: null,
    });
    const [doubleTapState, setDoubleTapState] = useState<DoubleTapState>({
        lastTapTime: 0,
        lastTapStep: null,
    });
    const longPressTimerRef = useRef<number | null>(null);
    const [stepsPerPage, setStepsPerPage] = useState(16);
    /**
     * Calculate how many rows can fit in the available space
     * Dynamically adjusts based on viewport height
     */
    useEffect(() => {
        const calculateStepsPerPage = () => {
            // Get viewport height
            const viewportHeight = window.innerHeight;
            // Estimate heights of other UI elements (conservative estimates)
            const navbarHeight = 80; // Approximate navbar height
            const transportBarHeight = 80; // Top transport bar
            const trackHeaderHeight = 80; // Track header with controls
            const bottomTabsHeight = 70; // Bottom tabs navigation
            const paginationHeight = 60; // Pagination controls (if shown)
            const moreInfoButtonHeight = 100; // More info button at bottom + extra buffer
            const padding = 60; // Additional padding/margins + safety buffer
            // Calculate available height for grid
            const availableHeight = viewportHeight - (navbarHeight +
                transportBarHeight +
                trackHeaderHeight +
                bottomTabsHeight +
                paginationHeight +
                moreInfoButtonHeight +
                padding);
            // Cell dimensions (from CSS)
            const cellSize = 48; // Base cell size
            const cellGap = 8; // Gap between rows
            const rowHeight = cellSize + cellGap;
            // Calculate how many rows can fit (minimum 2, maximum based on available space)
            const maxRows = Math.floor(availableHeight / rowHeight);
            const rows = Math.max(2, Math.min(maxRows, 6)); // Min 2 rows, max 6 rows (reduced from 8)
            // Each row shows 8 steps (columns)
            const calculatedStepsPerPage = rows * 8;
            setStepsPerPage(calculatedStepsPerPage);
        };
        // Calculate on mount and window resize
        calculateStepsPerPage();
        window.addEventListener('resize', calculateStepsPerPage);
        return () => {
            window.removeEventListener('resize', calculateStepsPerPage);
        };
    }, []);
    const totalPages = Math.ceil(steps / stepsPerPage);
    const showPagination = steps > stepsPerPage;
    /**
     * Auto-switch page when playback reaches a step on a different page
     * Requirement 10.6: Auto-paging during playback
     *
     * State Preservation (Requirement 10.7):
     * - Page position persists when switching tabs (managed by useMobileState)
     * - Only auto-switches when playback is active (isPlaying is true)
     * - Manual page changes are preserved until playback requires a different page
     */
    useEffect(() => {
        // ONLY auto-page during active playback
        // Do NOT run this effect when playback is stopped
        if (!isPlaying || !showPagination) {
            return;
        }
        // Only auto-page when currentStep is actively playing (> 0)
        if (currentStep > 0) {
            const stepPage = Math.floor(currentStep / stepsPerPage);
            console.log('[StepGrid] Calculated stepPage:', stepPage, 'currentPage:', currentPage);
            if (stepPage !== currentPage) {
                console.log('[StepGrid] AUTO-PAGING: Changing from page', currentPage, 'to page', stepPage);
                onPageChange(stepPage);
            }
        }
    }, [isPlaying, currentStep, showPagination, currentPage, stepsPerPage, onPageChange]);
    /**
     * Calculate visible steps based on pagination
     * Requirement 9.5: Display all steps if they fit on one page
     * Requirement 10.2: Display calculated steps per page when steps exceed one page
     */
    const visibleSteps = useMemo(() => {
        // If total steps fit on one page, show all steps
        if (steps <= stepsPerPage) {
            return Array.from({ length: steps }, (_, i) => i);
        }
        // Otherwise, show current page
        const startStep = currentPage * stepsPerPage;
        const endStep = Math.min(startStep + stepsPerPage, steps);
        return Array.from({ length: endStep - startStep }, (_, i) => startStep + i);
    }, [steps, currentPage, stepsPerPage]);
    /**
     * Handle note edit (long press, double-tap, or right-click)
     */
    const handleNoteEdit = useCallback((step: number) => {
        setNotePickerState({
            isOpen: true,
            step,
        });
    }, []);
    /**
     * Handle cell tap/click
     * Requirement 9.2: Toggle note on/off when cell is tapped
     * Also handles double-tap detection to open note picker
     */
    const handleCellClick = useCallback((step: number) => {
        // Only toggle if not dragging
        if (!dragState.isDragging) {
            const now = Date.now();
            const DOUBLE_TAP_DELAY = 300; // 300ms window for double-tap
            // Check if this is a double-tap
            if (doubleTapState.lastTapStep === step &&
                now - doubleTapState.lastTapTime < DOUBLE_TAP_DELAY) {
                // Double-tap detected - open note picker
                handleNoteEdit(step);
                // Reset double-tap state
                setDoubleTapState({
                    lastTapTime: 0,
                    lastTapStep: null,
                });
            }
            else {
                // Single tap - toggle cell
                onCellToggle(step);
                // Update double-tap state
                setDoubleTapState({
                    lastTapTime: now,
                    lastTapStep: step,
                });
            }
        }
    }, [dragState.isDragging, doubleTapState, onCellToggle, handleNoteEdit]);
    /**
     * Handle note selection from picker
     */
    const handleNoteSelect = useCallback((note: string) => {
        if (notePickerState.step !== null) {
            onNoteChange(notePickerState.step, note);
        }
    }, [notePickerState.step, onNoteChange]);
    /**
     * Handle note picker close
     */
    const handleNotePickerClose = useCallback(() => {
        setNotePickerState({
            isOpen: false,
            step: null,
        });
    }, []);
    /**
     * Handle drag start
     * Requirement 9.6: Support drag-to-paint for multiple cells
     */
    const handleDragStart = useCallback((step: number, _e: React.MouseEvent | React.TouchEvent) => {
        // Don't prevent default to allow touch events to work properly
        // Determine the value to paint based on the starting cell
        const dragValue = !gridState[step];
        setDragState({
            isDragging: true,
            dragStart: step,
            dragEnd: step,
            dragValue,
        });
    }, [gridState]);
    /**
     * Handle long press start
     */
    const handleLongPressStart = useCallback((step: number) => {
        // Clear any existing timer
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
        }
        // Start long press timer (500ms)
        longPressTimerRef.current = window.setTimeout(() => {
            handleNoteEdit(step);
            longPressTimerRef.current = null;
        }, 500);
    }, [handleNoteEdit]);
    /**
     * Handle long press cancel
     */
    const handleLongPressCancel = useCallback(() => {
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);
    /**
     * Cleanup long press timer on unmount
     */
    useEffect(() => {
        return () => {
            if (longPressTimerRef.current) {
                window.clearTimeout(longPressTimerRef.current);
            }
        };
    }, []);
    /**
     * Handle drag over
     * Requirement 9.6: Support drag-to-paint for multiple cells
     */
    const handleDragOver = useCallback((step: number) => {
        if (dragState.isDragging) {
            setDragState(prev => ({
                ...prev,
                dragEnd: step,
            }));
        }
    }, [dragState.isDragging]);
    /**
     * Handle drag end
     * Requirement 9.6: Apply drag-to-paint changes
     */
    useEffect(() => {
        const handleDragEnd = () => {
            if (dragState.isDragging &&
                dragState.dragStart !== null &&
                dragState.dragEnd !== null &&
                dragState.dragValue !== null) {
                const start = Math.min(dragState.dragStart, dragState.dragEnd);
                const end = Math.max(dragState.dragStart, dragState.dragEnd);
                // Only apply drag if we moved to other cells
                if (start !== end) {
                    // Toggle all cells in the drag range to the drag value
                    for (let step = start; step <= end; step++) {
                        // Only toggle if the cell doesn't already have the target value
                        if (gridState[step] !== dragState.dragValue) {
                            onCellToggle(step);
                        }
                    }
                }
            }
            setDragState({
                isDragging: false,
                dragStart: null,
                dragEnd: null,
                dragValue: null,
            });
        };
        if (dragState.isDragging) {
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchend', handleDragEnd);
            return () => {
                document.removeEventListener('mouseup', handleDragEnd);
                document.removeEventListener('touchend', handleDragEnd);
            };
        }
        return undefined;
    }, [dragState, gridState, onCellToggle]);
    /**
     * Check if a step is in the drag range
     */
    const isInDragRange = useCallback((step: number): boolean => {
        if (!dragState.isDragging || dragState.dragStart === null || dragState.dragEnd === null) {
            return false;
        }
        const start = Math.min(dragState.dragStart, dragState.dragEnd);
        const end = Math.max(dragState.dragStart, dragState.dragEnd);
        return step >= start && step <= end;
    }, [dragState]);
    /**
     * Render a single cell
     */
    const renderCell = (step: number) => {
        const isActive = gridState[step] ?? false;
        const note = noteState[step] ?? null;
        const isCurrent = step === currentStep;
        const inDragRange = isInDragRange(step);
        const noteColorData = isActive ? getNoteColorData(note) : { color: 'default', hue: 0 };
        // Build CSS classes
        const cellClasses = [
            'step-grid-cell',
            isActive && 'active',
            isCurrent && 'current-step',
            inDragRange && 'drag-highlight',
        ]
            .filter(Boolean)
            .join(' ');
        return (<div key={step} className={cellClasses} data-step={step} data-note-color={noteColorData.color} style={isActive && noteColorData.hue > 0
                ? { '--note-hue': noteColorData.hue } as React.CSSProperties
                : undefined} onClick={() => handleCellClick(step)} onMouseDown={(e) => handleDragStart(step, e)} onMouseEnter={() => handleDragOver(step)} onTouchStart={(e) => {
                handleDragStart(step, e);
                handleLongPressStart(step);
            }} onTouchMove={(e) => {
                // Cancel long press on move
                handleLongPressCancel();
                // Get the touch position and find the element under it
                const touch = e.touches[0];
                if (touch) {
                    const element = document.elementFromPoint(touch.clientX, touch.clientY);
                    if (element && element.hasAttribute('data-step')) {
                        const touchedStep = parseInt(element.getAttribute('data-step') || '0', 10);
                        handleDragOver(touchedStep);
                    }
                }
            }} onTouchEnd={() => {
                handleLongPressCancel();
            }} onTouchCancel={() => {
                handleLongPressCancel();
            }} onContextMenu={(e) => {
                e.preventDefault();
                handleNoteEdit(step);
            }} role="button" aria-label={`Step ${step + 1}${isActive ? ` active note ${note}` : ' inactive'}${isCurrent ? ' currently playing' : ''}`} aria-pressed={isActive} tabIndex={0} onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCellClick(step);
                }
            }}>
        {isActive && note ? (<span className="step-grid-cell-note">{note}</span>) : null}
      </div>);
    };
    return (<>
      <div className="step-grid-container" ref={gridRef} role="grid" aria-label={`Step grid for track ${trackIndex + 1}`}>
        <div className="step-grid">
          {visibleSteps.map(step => renderCell(step))}
        </div>
        
        {/* Display pagination when steps > 16 - Requirement 10.1 */}
        {showPagination && (<StepPagination currentPage={currentPage} totalPages={totalPages} stepsPerPage={stepsPerPage} totalSteps={steps} onPageChange={onPageChange}/>)}
      </div>

      {/* Note Picker Menu */}
      {notePickerState.isOpen && notePickerState.step !== null && (<NotePickerMenu currentNote={noteState[notePickerState.step] || 'C4'} trackIndex={trackIndex} stepIndex={notePickerState.step} onNoteSelect={handleNoteSelect} onClose={handleNotePickerClose}/>)}
    </>);
};
